import React, { useState } from 'react';
import { Gift, Check, X, Loader2 } from 'lucide-react';
import { PrimaryButton } from './brand/BrandButton';
import { Heading, BodyText, SmallText } from './brand/BrandTypography';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PromoCodeEntry - Component for users to enter promo codes
 * Used on settings page or during onboarding
 */
export const PromoCodeEntry = ({ onSuccess, compact = false }) => {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;

        setLoading(true);
        setResult(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/promo/apply`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ code: code.trim() })
            });

            const data = await response.json();
            setResult(data);

            if (data.success) {
                toast.success(data.message);
                if (onSuccess) {
                    onSuccess(data);
                }
                // Reload to update subscription status
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error('Error applying promo code:', error);
            toast.error('Failed to apply promo code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (compact) {
        return (
            <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Enter promo code"
                    className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/50"
                    disabled={loading}
                    data-testid="promo-code-input"
                />
                <PrimaryButton 
                    type="submit" 
                    disabled={loading || !code.trim()}
                    data-testid="promo-code-submit"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </PrimaryButton>
            </form>
        );
    }

    return (
        <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFBB98] to-[#E09060] flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                    <Heading level={4} className="text-[#1D6B6B]">Have a promo code?</Heading>
                    <SmallText className="text-[#7D8E95]">Get free access in exchange for feedback</SmallText>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="Enter your promo code"
                        className="w-full px-4 py-3 rounded-xl bg-white/50 border border-[#7D8E95]/20 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/50 font-mono text-lg tracking-wider"
                        disabled={loading}
                        data-testid="promo-code-input"
                    />
                </div>

                {result && (
                    <div className={`p-4 rounded-xl flex items-start gap-3 ${
                        result.success 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-red-50 border border-red-200'
                    }`}>
                        {result.success ? (
                            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                            <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                            <BodyText className={result.success ? 'text-green-800' : 'text-red-800'}>
                                {result.message}
                            </BodyText>
                            {result.success && result.free_until && (
                                <SmallText className="text-green-600 mt-1">
                                    Free until: {new Date(result.free_until).toLocaleDateString('en-AU', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </SmallText>
                            )}
                        </div>
                    </div>
                )}

                <PrimaryButton 
                    type="submit" 
                    className="w-full"
                    disabled={loading || !code.trim()}
                    data-testid="promo-code-submit"
                >
                    {loading ? (
                        <span className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Applying...
                        </span>
                    ) : (
                        'Apply Promo Code'
                    )}
                </PrimaryButton>
            </form>

            <SmallText className="text-[#7D8E95] text-center mt-4">
                Promo codes give you free premium access. In return, we ask for quick feedback on the tools you use.
            </SmallText>
        </div>
    );
};

export default PromoCodeEntry;
